import type React from 'react';
import { useContext, useEffect } from 'react';
import { Footer, StyledContainer } from '@zg-rentals/zrs-comet';
import BlockedUserModal from '@zg-rentals/blocked-user-modal';
import { StickyContainer } from 'react-sticky';
import { AppContext } from '../../contexts/AppContextProvider';
import StringConstants from '../../constants/StringConstants';
import Link from 'next/link';
import { AppLayoutContainer } from '@zg-rentals/zrm-utils-js';
import useFeatureFlagsHelper from '../../hooks/useFeatureFlagsHelper';
import useModal from '../../hooks/useModal';
import { ModalType } from '../../../src/types/ModalType';
import CustomizedBlockedUserModal from '../Modals/BlockedUserModal';
import { useAuth } from '@zg-rentals/auth-comet';
import { AppDownloadUpsellBanner } from '@zg-rentals/app-download-upsell-banner';

export default function Layout({
  children,
  showFooter = true,
  showHeader = true,
}: {
  children: React.ReactNode;
  showFooter?: boolean;
  showHeader?: boolean;
}) {
  const { paths } = useContext(AppContext);
  const { canAccessBlockedUserModalFromRJSModule } = useFeatureFlagsHelper();
  const { activeModal, setActiveModal } = useModal();

  const hostName = typeof window !== 'undefined' && window !== null ? window.location.host : 'www.zillow.com';
  const pathname = typeof window !== 'undefined' && window !== null ? window.location.href : '';

  const { user } = useAuth();
  const { isEnterpriseCustomer, isUserBlocked, restrictions } = user ?? {};
  const { isAllowed } = restrictions ?? {};

  const shouldDisplayBlockedUserModalFromModule =
    isAllowed === false && canAccessBlockedUserModalFromRJSModule && isUserBlocked;
  const shouldDisplayCustomizedBlockedUserModal = !canAccessBlockedUserModalFromRJSModule && isUserBlocked;

  const handleLogoutClick = (): void => {
    window.location.href = '/Logout.htm';
  };

  useEffect(() => {
    if (shouldDisplayCustomizedBlockedUserModal) {
      setActiveModal(ModalType.CustomizedBlockedModal);
    } else if (shouldDisplayBlockedUserModalFromModule) {
      setActiveModal(ModalType.BlockedModalFromModule);
    }
  }, [shouldDisplayCustomizedBlockedUserModal, shouldDisplayBlockedUserModalFromModule, activeModal]);

  return (
    <div id="app-root">
      <StickyContainer id="sticky-container">
        <StyledContainer id="App">
          <AppDownloadUpsellBanner />

          {/* use the blocked user modal from the blocked-user-modal module, default to use our version if the feature flag value is false */}
          {activeModal === ModalType.BlockedModalFromModule && restrictions && (
            <BlockedUserModal restrictions={restrictions} />
          )}
          {activeModal === ModalType.CustomizedBlockedModal && <CustomizedBlockedUserModal />}

          {showHeader && user != null && (
            <AppLayoutContainer
              altLogo={!isEnterpriseCustomer}
              brand={StringConstants.UNFORMATTED_BRAND_NAMES.zillow}
              userModel={user}
              useRelativePaths={false}
              reactRouterLink={Link}
              host={hostName}
              pathname={pathname}
              proxyPath={paths.proxyPath}
              handleLogoutClick={handleLogoutClick}
              displayMainMyProperties={true}
              zgApi={process.env.NEXT_PUBLIC_ZG_API_FOR_HEADER}
            >
              <main>{children}</main>
              {showFooter && <Footer />}
            </AppLayoutContainer>
          )}
        </StyledContainer>
      </StickyContainer>
    </div>
  );
}
